import { motion } from "framer-motion";
import { ArrowRight, FileText, RefreshCw, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { StatusPill } from "@/components/StatusPill";
import { useSession } from "@/context/SessionContext";
import { fetchCandidateDashboard, type CandidateDashboardResponse } from "@/lib/api";

const AGREEMENT_TEMPLATES = [
  {
    slug: "internship-agreement-india",
    label: "Internship Agreement",
    to: "/candidate/internship"
  },
  {
    slug: "addendum-offer-letter-india",
    label: "Addendum Agreement",
    to: "/candidate/addendum"
  }
] as const;

function MetricCard(props: { label: string; value: string; icon: ReactNode }) {
  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate/70">
            {props.label}
          </p>
          <p className="mt-3 text-3xl font-semibold tracking-tight text-ink">{props.value}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-line/70 bg-cloud/70 text-royal">
          {props.icon}
        </div>
      </div>
    </div>
  );
}

function formatMaybeDate(value?: string | null) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export function CandidateDashboardPage() {
  const { session } = useSession();
  const [dashboards, setDashboards] = useState<Record<string, CandidateDashboardResponse | null>>(
    () => Object.fromEntries(AGREEMENT_TEMPLATES.map((item) => [item.slug, null]))
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadDashboard();
  }, []);

  async function loadDashboard(options?: { silent?: boolean }) {
    if (!options?.silent) setIsLoading(true);
    setError(null);

    try {
      const results = await Promise.allSettled(
        AGREEMENT_TEMPLATES.map((item) => fetchCandidateDashboard(item.slug))
      );
      const nextDashboards: Record<string, CandidateDashboardResponse | null> = {};

      for (const [index, result] of results.entries()) {
        const slug = AGREEMENT_TEMPLATES[index].slug;
        nextDashboards[slug] = result.status === "fulfilled" ? result.value : null;
      }

      setDashboards(nextDashboards);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Unable to load dashboard.");
    } finally {
      if (!options?.silent) setIsLoading(false);
    }
  }

  async function handleRefresh() {
    setIsRefreshing(true);
    await loadDashboard({ silent: true });
    setIsRefreshing(false);
  }

  const candidateName = session?.role === "candidate" ? session.user.fullName : "Candidate";
  const accountStatusLabel = useMemo(() => {
    if (session?.role === "candidate") {
      return session.user.status.replace(/_/g, " ");
    }

    const firstDashboard = dashboards[AGREEMENT_TEMPLATES[0].slug];
    return firstDashboard?.summary.accountStatusLabel ?? "—";
  }, [dashboards, session]);

  const submittedAgreementsCount = useMemo(() => {
    return AGREEMENT_TEMPLATES.reduce((sum, item) => {
      const status = dashboards[item.slug]?.summary.latestSubmittedStatus;
      return sum + (status === "submitted" ? 1 : 0);
    }, 0);
  }, [dashboards]);

  function getAgreementStatusLabel(status: CandidateDashboardResponse["summary"]["latestSubmittedStatus"]) {
    return status === "submitted" ? "Submitted" : status === "draft" ? "Draft in progress" : "Not started";
  }

  const primaryNextAction = useMemo(() => {
    const internshipDashboard = dashboards["internship-agreement-india"];
    if (!internshipDashboard) return null;
    const status = internshipDashboard.summary.latestSubmittedStatus;
    return status === "submitted"
      ? { label: "Review internship agreement", to: "/candidate/internship" }
      : status === "draft"
        ? { label: "Continue internship draft", to: "/candidate/internship" }
        : { label: "Start internship agreement", to: "/candidate/internship" };
  }, [dashboards]);

  if (isLoading) {
    return (
      <div className="mx-auto flex min-h-[60vh] items-center justify-center px-6">
        <div className="panel max-w-md text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.24em] text-moss/70">
            Loading dashboard
          </p>
          <h1 className="mt-4 text-3xl text-ink">Preparing your workspace</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-8">
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="surface px-6 py-6 md:px-7"
        data-testid="candidate-dashboard-hero"
        initial={{ opacity: 0, y: 18 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="eyebrow">Candidate</p>
            <h1 className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl">
              Welcome back, {candidateName}
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
              Track your agreement status and update your draft from your secure candidate
              workspace.
            </p>
          </div>

          <button className="btn-secondary px-4 py-2.5" onClick={() => void handleRefresh()} type="button">
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </motion.div>

      {error ? (
        <div className="mt-6 rounded-[24px] border border-rose-200 bg-rose-50 px-6 py-5 text-rose-800">
          <p className="text-sm font-semibold">Unable to load dashboard</p>
          <p className="mt-1 text-sm leading-6">{error}</p>
        </div>
      ) : null}

      {Object.values(dashboards).some(Boolean) ? (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={<ShieldCheck className="h-5 w-5" />}
              label="Account Status"
              value={accountStatusLabel}
            />
            <MetricCard
              icon={<FileText className="h-5 w-5" />}
              label="Agreements"
              value={`${submittedAgreementsCount}/${AGREEMENT_TEMPLATES.length}`}
            />
            <MetricCard
              icon={<FileText className="h-5 w-5" />}
              label="Templates"
              value={`${AGREEMENT_TEMPLATES.length}`}
            />
          </div>

          <div className="mt-6">
            <section className="surface p-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="eyebrow">Next Step</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
                    Keep your agreement up to date
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
                    Drafts autosave while you work. When you submit, the portal locks your record
                    and stores a signed PDF for compliance.
                  </p>
                </div>
                {primaryNextAction ? (
                  <Link className="btn-primary px-4 py-2.5" to={primaryNextAction.to}>
                    {primaryNextAction.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {AGREEMENT_TEMPLATES.map((item) => {
                  const agreementDashboard = dashboards[item.slug];
                  const agreementStatus = agreementDashboard?.summary.latestSubmittedStatus ?? "not_submitted";
                  return (
                    <div
                      className="rounded-[24px] border border-line/70 bg-white/70 p-5"
                      key={item.slug}
                    >
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate/70">
                        {item.label}
                      </p>
                      <p className="mt-3 text-sm font-semibold text-ink">
                        Status: {getAgreementStatusLabel(agreementStatus)}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate">
                        Last updated:{" "}
                        <span className="font-semibold text-ink">
                          {formatMaybeDate(agreementDashboard?.summary.latestSubmissionUpdatedAt)}
                        </span>
                      </p>
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                        {agreementDashboard ? (
                          <span className="rounded-full border border-line/70 bg-white px-3 py-1 text-xs font-semibold text-slate">
                            Active v{agreementDashboard.summary.activeTemplateVersionNumber}
                          </span>
                        ) : (
                          <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-xs font-semibold text-rose-700">
                            Not available
                          </span>
                        )}
                        <Link
                          className="inline-flex items-center gap-2 text-sm font-semibold text-royal transition hover:text-navy"
                          to={item.to}
                        >
                          Open
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  );
                })}

                <div className="rounded-[24px] border border-line/70 bg-white/70 p-5 md:col-span-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate/70">
                    Portal Status
                  </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    {session?.role === "candidate" ? (
                      <StatusPill status={session.user.status} />
                    ) : null}
                    <Link
                      className="inline-flex items-center gap-2 text-sm font-semibold text-royal transition hover:text-navy"
                      to="/profile"
                    >
                      Update your profile
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </>
      ) : null}
    </div>
  );
}
